/** @module @liquidefy/code-editor/project/project-env @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  EditorResult,
  EnvironmentState,
  EnvironmentId,
} from '../code-editor-types.js';

import { ok, err, generateId, asEnvironmentId, now } from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 INTERNAL TYPES
// ═══════════════════════════════════════════════════════════════════════════════

/** A single environment variable entry. */
export interface EnvVariable {
  readonly key: string;
  readonly value: string;
  readonly isSecret: boolean;
  readonly source: string;
}

/** A parsed .env file. */
export interface EnvFile {
  readonly path: string;
  readonly name: string;
  readonly variables: Record<string, string>;
  readonly loadedAt: number;
}

/** Result of running secret detection on a set of variables. */
export interface SecretDetectionResult {
  readonly suspectedSecrets: string[];
  readonly scanned: number;
  readonly patterns: string[];
}

/** Multi-environment container for managing multiple EnvironmentStates. */
export interface EnvironmentContainer {
  readonly environments: Map<string, EnvironmentState>;
  readonly activeEnvName: string;
  readonly rootPath: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 STATE FACTORY — create environment state and containers
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a fresh EnvironmentContainer for the given project root path.
 * Starts with an empty 'default' environment.
 */
export function createEnvironmentState(rootPath: string): EnvironmentContainer {
  const defaultEnv: EnvironmentState = {
    id: asEnvironmentId(generateId('env')),
    name: 'default',
    variables: {},
    isActive: true,
    source: rootPath,
    hasSecrets: false,
  };
  const environments = new Map<string, EnvironmentState>();
  environments.set('default', defaultEnv);
  return {
    environments,
    activeEnvName: 'default',
    rootPath,
  };
}

/**
 * Create a single EnvironmentState from a parsed variables map.
 */
export function makeEnvironmentState(
  name: string,
  variables: Record<string, string>,
  source: string
): EnvironmentState {
  const secrets = detectSecrets(variables);
  return {
    id: asEnvironmentId(generateId('env')),
    name,
    variables,
    isActive: false,
    source,
    hasSecrets: secrets.suspectedSecrets.length > 0,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 LOADING — parse .env file content into state
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Load an .env file content string into the environment container.
 * Parses the file, creates a new EnvironmentState, and adds it to the container.
 * The environment name is derived from the file path (e.g., .env.production → production).
 */
export function loadEnvFile(
  container: EnvironmentContainer,
  filePath: string,
  content: string
): EditorResult<EnvironmentContainer> {
  if (!content && content !== '') {
    return err('INVALID_CONTENT', 'Env file content cannot be null');
  }
  const envName = deriveEnvName(filePath);
  const variables = parseDotEnv(content);
  const envState = makeEnvironmentState(envName, variables, filePath);
  const environments = new Map(container.environments);
  environments.set(envName, envState);
  return ok(
    { ...container, environments },
    'ENV_FILE_LOADED',
    `Loaded env file "${filePath}" as environment "${envName}"`
  );
}

/**
 * Parse a .env format string into a key-value map.
 * Handles quoted values, inline comments, and escape sequences.
 */
export function parseDotEnv(content: string): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx < 1) continue;
    const key = trimmed.slice(0, eqIdx).trim();
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) continue;
    let value = trimmed.slice(eqIdx + 1).trim();
    // Strip inline comment (not inside quotes)
    if (!value.startsWith('"') && !value.startsWith("'")) {
      const commentIdx = value.indexOf(' #');
      if (commentIdx >= 0) value = value.slice(0, commentIdx).trim();
    }
    // Handle quoted values
    if ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1).replace(/\\n/g, '\n').replace(/\\t/g, '\t');
    }
    vars[key] = value;
  }
  return vars;
}

/**
 * Serialize a variables map back to .env format.
 */
export function serializeDotEnv(variables: Record<string, string>): string {
  return Object.entries(variables)
    .map(([k, v]) => {
      const needsQuotes = /\s|#/.test(v);
      const val = needsQuotes ? `"${v.replace(/"/g, '\\"')}"` : v;
      return `${k}=${val}`;
    })
    .join('\n');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 VARIABLE MANAGEMENT — resolve, set, delete
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Resolve a variable from the active or specified environment.
 * Resolution order: specified env → default env → undefined.
 */
export function resolveVariable(
  container: EnvironmentContainer,
  name: string,
  envName?: string
): string | undefined {
  const targetName = envName ?? container.activeEnvName;
  const target = container.environments.get(targetName);
  if (target?.variables[name] !== undefined) return target.variables[name];
  // Fall back to default
  if (targetName !== 'default') {
    const def = container.environments.get('default');
    if (def?.variables[name] !== undefined) return def.variables[name];
  }
  return undefined;
}

/**
 * Set a variable in the specified environment (or active env).
 */
export function setVariable(
  container: EnvironmentContainer,
  name: string,
  value: string,
  envName?: string
): EditorResult<EnvironmentContainer> {
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
    return err('INVALID_VAR_NAME', `"${name}" is not a valid environment variable name`);
  }
  const targetName = envName ?? container.activeEnvName;
  const target = container.environments.get(targetName);
  if (!target) {
    return err('ENV_NOT_FOUND', `Environment "${targetName}" does not exist`);
  }
  const variables = { ...target.variables, [name]: value };
  const secrets = detectSecrets(variables);
  const updated: EnvironmentState = {
    ...target,
    variables,
    hasSecrets: secrets.suspectedSecrets.length > 0,
  };
  const environments = new Map(container.environments);
  environments.set(targetName, updated);
  return ok(
    { ...container, environments },
    'VAR_SET',
    `Set ${name} in environment "${targetName}"`
  );
}

/**
 * Delete a variable from the specified environment (or active env).
 */
export function deleteVariable(
  container: EnvironmentContainer,
  name: string,
  envName?: string
): EditorResult<EnvironmentContainer> {
  const targetName = envName ?? container.activeEnvName;
  const target = container.environments.get(targetName);
  if (!target) {
    return err('ENV_NOT_FOUND', `Environment "${targetName}" does not exist`);
  }
  if (!(name in target.variables)) {
    return err('VAR_NOT_FOUND', `Variable "${name}" not found in environment "${targetName}"`);
  }
  const { [name]: _removed, ...remaining } = target.variables;
  const secrets = detectSecrets(remaining);
  const updated: EnvironmentState = {
    ...target,
    variables: remaining,
    hasSecrets: secrets.suspectedSecrets.length > 0,
  };
  const environments = new Map(container.environments);
  environments.set(targetName, updated);
  return ok(
    { ...container, environments },
    'VAR_DELETED',
    `Deleted ${name} from environment "${targetName}"`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 ENVIRONMENT SWITCHING — activate, list, get effective
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Switch the active environment.
 * Returns an error if the target environment doesn't exist.
 */
export function switchEnvironment(
  container: EnvironmentContainer,
  envName: string
): EditorResult<EnvironmentContainer> {
  if (!container.environments.has(envName)) {
    return err('ENV_NOT_FOUND', `Environment "${envName}" does not exist`);
  }
  // Mark new active, deactivate old
  const environments = new Map<string, EnvironmentState>();
  for (const [name, env] of container.environments) {
    environments.set(name, { ...env, isActive: name === envName });
  }
  return ok(
    { ...container, environments, activeEnvName: envName },
    'ENV_SWITCHED',
    `Switched to environment "${envName}"`
  );
}

/**
 * List all available environment names.
 */
export function listEnvironments(container: EnvironmentContainer): string[] {
  return Array.from(container.environments.keys());
}

/**
 * Get the effective merged environment variables for the active environment.
 * Merges default → active env, with active env taking precedence.
 */
export function getEffectiveEnv(container: EnvironmentContainer): Record<string, string> {
  const result: Record<string, string> = {};
  // Start with default
  const defaultEnv = container.environments.get('default');
  if (defaultEnv) Object.assign(result, defaultEnv.variables);
  // Override with active
  const active = container.environments.get(container.activeEnvName);
  if (active && active.name !== 'default') Object.assign(result, active.variables);
  return result;
}

/**
 * Export a specific environment to .env format.
 */
export function exportEnv(container: EnvironmentContainer, envName: string): EditorResult<string> {
  const env = container.environments.get(envName);
  if (!env) {
    return err('ENV_NOT_FOUND', `Environment "${envName}" does not exist`);
  }
  const content = serializeDotEnv(env.variables);
  return ok(content, 'ENV_EXPORTED', `Exported environment "${envName}"`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 SECRET DETECTION — flag potential secrets by key name
// ═══════════════════════════════════════════════════════════════════════════════

/** Regex patterns for variable names that likely hold secrets. */
const SECRET_PATTERNS: readonly RegExp[] = [
  /API[_-]?KEY/i,
  /API[_-]?SECRET/i,
  /SECRET[_-]?KEY/i,
  /ACCESS[_-]?KEY/i,
  /ACCESS[_-]?TOKEN/i,
  /AUTH[_-]?TOKEN/i,
  /\bTOKEN\b/i,
  /PASSWORD/i,
  /PASSWD/i,
  /PRIVATE[_-]?KEY/i,
  /SIGNING[_-]?KEY/i,
  /ENCRYPTION[_-]?KEY/i,
  /DATABASE[_-]?URL/i,
  /DB[_-]?PASSWORD/i,
  /DB[_-]?PASS/i,
  /STRIPE[_-]?KEY/i,
  /AWS[_-]?SECRET/i,
  /CLIENT[_-]?SECRET/i,
  /WEBHOOK[_-]?SECRET/i,
  /JWT[_-]?SECRET/i,
  /COOKIE[_-]?SECRET/i,
  /SESSION[_-]?SECRET/i,
  /SSH[_-]?KEY/i,
  /GPG[_-]?KEY/i,
  /CERT/i,
  /CREDENTIALS/i,
  /\bSECRET\b/i,
];

/**
 * Detect which variable names in a map look like they hold secrets.
 */
export function detectSecrets(variables: Record<string, string>): SecretDetectionResult {
  const suspected = Object.keys(variables).filter((key) =>
    SECRET_PATTERNS.some((pattern) => pattern.test(key))
  );
  return {
    suspectedSecrets: suspected,
    scanned: Object.keys(variables).length,
    patterns: SECRET_PATTERNS.map((p) => p.source),
  };
}

/**
 * Mask secret values in a variables map, replacing their values with '***'.
 * Returns a new map safe for display or logging.
 */
export function maskSecrets(variables: Record<string, string>): Record<string, string> {
  const secrets = new Set(detectSecrets(variables).suspectedSecrets);
  const masked: Record<string, string> = {};
  for (const [k, v] of Object.entries(variables)) {
    masked[k] = secrets.has(k) ? '***' : v;
  }
  return masked;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 UTILITIES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Derive an environment name from a .env file path.
 * Examples: .env → default, .env.local → local, .env.production → production
 */
export function deriveEnvName(filePath: string): string {
  const fileName = filePath.split('/').pop() ?? '';
  if (fileName === '.env') return 'default';
  const match = fileName.match(/^\.env\.(.+)$/);
  if (match) return match[1]!;
  return fileName.replace(/^\./, '');
}

/**
 * Get all standard .env file names for a project.
 */
export function standardEnvFiles(rootPath: string): string[] {
  return [
    `${rootPath}/.env`,
    `${rootPath}/.env.local`,
    `${rootPath}/.env.development`,
    `${rootPath}/.env.development.local`,
    `${rootPath}/.env.test`,
    `${rootPath}/.env.test.local`,
    `${rootPath}/.env.production`,
    `${rootPath}/.env.production.local`,
  ];
}

/**
 * Get the count of variables in an environment.
 */
export function variableCount(env: EnvironmentState): number {
  return Object.keys(env.variables).length;
}

/**
 * Get the count of suspected secrets in an environment.
 */
export function secretCount(env: EnvironmentState): number {
  return detectSecrets(env.variables).suspectedSecrets.length;
}
